import { useEffect, useRef } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { streamJsonSseWithRetry } from '../../../../utils/SseStreamUtil';

async function hasAuth() {
  try {
    const { tokens } = await fetchAuthSession();
    return Boolean(tokens?.idToken);
  } catch {
    return false;
  }
}

/**
 * Lifecycle-scoped SSE subscription used by {@link FormFlowShell} while
 * {@code session.status === 'TRANSITIONING'}. Connects to Phase 3's
 * {@code GET /form-flow/session/{fsid}/stream}, reroutes transition events to the caller,
 * and auto-disconnects on unmount or status change.
 *
 * Events: {@code step_ready} → caller refetches; {@code heartbeat} → no-op (connection liveness);
 * {@code error} → caller can show a retry button.
 */
export default function useFormFlowSse({ enabled, streamUrl, onEvent }) {
  const controllerRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !streamUrl) return undefined;

    const abortController = new AbortController();
    controllerRef.current = abortController;
    let cancelled = false;

    (async () => {
      const authed = await hasAuth();
      await streamJsonSseWithRetry({
        url: streamUrl,
        abortController,
        logLabel: 'FormFlow SSE',
        skipAuth: !authed,
        shouldReconnect: () => !cancelled,
        onEvent: (eventName, parsed) => {
          if (cancelled) return;
          onEventRef.current?.(eventName, parsed);
        },
      });
    })();

    return () => {
      cancelled = true;
      try { abortController.abort(); } catch { /* ignore */ }
      controllerRef.current = null;
    };
  }, [enabled, streamUrl]);
}
